import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, Menu, RefreshCw, Search, X } from "lucide-react";
import type { DouyinPerformanceRecord, TmallPerformanceRecord } from "@ailan/contracts";
import {
  getDouyinDashboard,
  getKuaishouDashboard,
  getPddDashboard,
  getTmallDashboard,
} from "../api/client";

type PlatformId = "tmall" | "jd" | "pdd" | "douyin" | "kuaishou" | "xiaohongshu" | "taogongchang";
type MetricId = "inquiry" | "response" | "satisfaction";

type DailyRecord = {
  id: string;
  platform: PlatformId;
  storeName: string;
  agentName: string;
  date: string;
  collectedAt: string;
  inquiryCount: number | null;
  avgReplySeconds: number | null;
  satisfactionRate: number | null;
};

type AgentRow = {
  key: string;
  platform: PlatformId;
  storeName: string;
  agentName: string;
  byDate: Map<string, DailyRecord>;
  pendingCapture: boolean;
};

type StoreDirectoryItem = {
  platform: PlatformId;
  storeName: string;
};

const platforms: { id: PlatformId; label: string; connected: boolean }[] = [
  { id: "tmall", label: "天猫", connected: true },
  { id: "jd", label: "京东", connected: false },
  { id: "pdd", label: "拼多多", connected: true },
  { id: "douyin", label: "抖音", connected: true },
  { id: "kuaishou", label: "快手", connected: true },
  { id: "xiaohongshu", label: "小红书", connected: false },
  { id: "taogongchang", label: "淘工厂", connected: false },
];

const platformLabels = Object.fromEntries(platforms.map((item) => [item.id, item.label])) as Record<PlatformId, string>;
const metricLabels: Record<MetricId, string> = {
  inquiry: "询单量",
  response: "平均响应",
  satisfaction: "满意度",
};

function normalizeCommerceRecord(
  item: TmallPerformanceRecord,
  platform: "tmall" | "pdd" | "kuaishou",
): DailyRecord {
  return {
    id: item.id,
    platform,
    storeName: item.storeName,
    agentName: item.agentName,
    date: item.periodEnd,
    collectedAt: item.collectedAt,
    inquiryCount: item.inquiryCount,
    avgReplySeconds: item.avgReplySeconds,
    satisfactionRate: platform === "pdd" ? null : item.satisfactionRate,
  };
}

function normalizeDouyinRecord(item: DouyinPerformanceRecord): DailyRecord {
  return {
    id: item.id,
    platform: "douyin",
    storeName: item.storeName,
    agentName: item.agentName,
    date: item.periodEnd,
    collectedAt: item.collectedAt,
    inquiryCount: null,
    avgReplySeconds: item.avgReplySeconds,
    satisfactionRate: item.satisfactionRate,
  };
}

function dateRange(start: string, end: string) {
  if (!start || !end || start > end) return [];
  const result: string[] = [];
  const cursor = new Date(`${start}T00:00:00Z`);
  const last = new Date(`${end}T00:00:00Z`);
  while (cursor <= last && result.length < 31) {
    result.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return result;
}

function shortDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return "暂无日期";
  const [, month, day] = value.split("-");
  return `${Number(month)}月${Number(day)}日`;
}

function valueOf(record: DailyRecord | undefined, metric: MetricId) {
  if (!record) return null;
  if (metric === "inquiry") return record.inquiryCount;
  if (metric === "response") return record.avgReplySeconds;
  return record.satisfactionRate;
}

function formatMetric(value: number | null, metric: MetricId) {
  if (value === null) return "--";
  if (metric === "inquiry") return new Intl.NumberFormat("zh-CN").format(value);
  if (metric === "response") return `${value.toFixed(1)}秒`;
  return `${value.toFixed(1)}%`;
}

function toneOf(value: number | null, metric: MetricId) {
  if (value === null) return "empty";
  if (metric === "response") return value > 20 ? "bad" : value > 12 ? "watch" : "good";
  if (metric === "satisfaction") return value < 90 ? "bad" : value < 95 ? "watch" : "good";
  return "normal";
}

function exportRows(rows: AgentRow[], dates: string[]) {
  const headers = ["平台", "店铺", "客服", "日期", "询单量", "平均响应（秒）", "满意度"];
  const body = rows.flatMap((row) => dates.map((date) => {
    const record = row.byDate.get(date);
    return [
      platformLabels[row.platform],
      row.storeName,
      row.agentName,
      date,
      record?.inquiryCount ?? "--",
      record?.avgReplySeconds ?? "--",
      record?.satisfactionRate ?? "--",
    ];
  }));
  const csv = "\ufeff" + [headers, ...body]
    .map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(","))
    .join("\r\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `客服每日对比_${dates[0] ?? ""}_${dates.at(-1) ?? ""}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export function DailyAgentComparisonPage({ onMenuOpen }: { onMenuOpen: () => void }) {
  const tmall = useQuery({ queryKey: ["tmall-dashboard"], queryFn: getTmallDashboard, refetchInterval: 60_000 });
  const kuaishou = useQuery({ queryKey: ["kuaishou-dashboard"], queryFn: getKuaishouDashboard, refetchInterval: 60_000 });
  const pdd = useQuery({ queryKey: ["pdd-dashboard"], queryFn: getPddDashboard, refetchInterval: 60_000 });
  const douyin = useQuery({ queryKey: ["douyin-dashboard"], queryFn: getDouyinDashboard, refetchInterval: 60_000 });
  const [platform, setPlatform] = useState<PlatformId | "all">("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [store, setStore] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<{ row: AgentRow; date: string; metric: MetricId } | null>(null);

  const records = useMemo<DailyRecord[]>(() => [
    ...(tmall.data?.records ?? []).map((item) => normalizeCommerceRecord(item, "tmall")),
    ...(kuaishou.data?.records ?? []).map((item) => normalizeCommerceRecord(item, "kuaishou")),
    ...(pdd.data?.records ?? []).map((item) => normalizeCommerceRecord(item, "pdd")),
    ...(douyin.data?.records ?? []).map(normalizeDouyinRecord),
  ], [douyin.data?.records, kuaishou.data?.records, pdd.data?.records, tmall.data?.records]);

  const dedupedRecords = useMemo(() => {
    const latest = new Map<string, DailyRecord>();
    records.forEach((record) => {
      const key = `${record.platform}\u0000${record.storeName}\u0000${record.agentName}\u0000${record.date}`;
      const current = latest.get(key);
      if (!current || record.collectedAt > current.collectedAt) latest.set(key, record);
    });
    return [...latest.values()];
  }, [records]);

  const storeDirectory = useMemo<StoreDirectoryItem[]>(() => {
    const directory = new Map<string, StoreDirectoryItem>();
    const add = (item: StoreDirectoryItem) => {
      const storeName = item.storeName.trim();
      if (!storeName) return;
      directory.set(`${item.platform}\u0000${storeName}`, { ...item, storeName });
    };

    (tmall.data?.stores ?? []).forEach((item) => add({ platform: "tmall", storeName: item.name }));
    (kuaishou.data?.stores ?? []).forEach((item) => add({ platform: "kuaishou", storeName: item.name }));
    (pdd.data?.stores ?? []).forEach((item) => add({ platform: "pdd", storeName: item.name }));
    (douyin.data?.stores ?? []).forEach((item) => add({ platform: "douyin", storeName: item.name }));
    dedupedRecords.forEach((item) => add({ platform: item.platform, storeName: item.storeName }));

    return [...directory.values()].sort((left, right) =>
      platforms.findIndex((item) => item.id === left.platform) - platforms.findIndex((item) => item.id === right.platform)
      || left.storeName.localeCompare(right.storeName, "zh-CN"));
  }, [dedupedRecords, douyin.data?.stores, kuaishou.data?.stores, pdd.data?.stores, tmall.data?.stores]);

  const platformStoreCounts = useMemo(() => {
    const counts = Object.fromEntries(platforms.map((item) => [item.id, 0])) as Record<PlatformId, number>;
    storeDirectory.forEach((item) => { counts[item.platform] += 1; });
    return counts;
  }, [storeDirectory]);

  const availableDates = useMemo(() => [...new Set(dedupedRecords.map((item) => item.date))].sort(), [dedupedRecords]);
  const defaultEnd = availableDates.at(-1) ?? "";
  const defaultStart = availableDates[Math.max(0, availableDates.length - 4)] ?? defaultEnd;
  const activeStart = startDate || defaultStart;
  const activeEnd = endDate || defaultEnd;
  const dates = useMemo(() => dateRange(activeStart, activeEnd), [activeEnd, activeStart]);

  const stores = useMemo(() => [...new Set(storeDirectory
    .filter((item) => platform === "all" || item.platform === platform)
    .map((item) => item.storeName))].sort((a, b) => a.localeCompare(b, "zh-CN")), [platform, storeDirectory]);

  const rows = useMemo(() => {
    const grouped = new Map<string, AgentRow>();
    const term = search.trim().toLowerCase();
    dedupedRecords
      .filter((item) => platform === "all" || item.platform === platform)
      .filter((item) => !store || item.storeName === store)
      .filter((item) => item.date >= activeStart && item.date <= activeEnd)
      .filter((item) => !term || `${item.agentName}${item.storeName}${platformLabels[item.platform]}`.toLowerCase().includes(term))
      .forEach((item) => {
        const key = `${item.platform}\u0000${item.storeName}\u0000${item.agentName}`;
        const row = grouped.get(key) ?? { key, platform: item.platform, storeName: item.storeName, agentName: item.agentName, byDate: new Map(), pendingCapture: false };
        row.byDate.set(item.date, item);
        grouped.set(key, row);
      });

    storeDirectory
      .filter((item) => platform === "all" || item.platform === platform)
      .filter((item) => !store || item.storeName === store)
      .filter((item) => !term || `${item.storeName}${platformLabels[item.platform]}`.toLowerCase().includes(term))
      .forEach((item) => {
        const hasAgent = [...grouped.values()].some((row) => row.platform === item.platform && row.storeName === item.storeName);
        if (hasAgent) return;
        const key = `${item.platform}\u0000${item.storeName}\u0000pending-capture`;
        grouped.set(key, {
          key,
          platform: item.platform,
          storeName: item.storeName,
          agentName: "--",
          byDate: new Map(),
          pendingCapture: true,
        });
      });

    return [...grouped.values()].sort((left, right) =>
      platforms.findIndex((item) => item.id === left.platform) - platforms.findIndex((item) => item.id === right.platform)
      || left.storeName.localeCompare(right.storeName, "zh-CN")
      || left.agentName.localeCompare(right.agentName, "zh-CN"));
  }, [activeEnd, activeStart, dedupedRecords, platform, search, store, storeDirectory]);

  const visibleAgentCount = rows.filter((row) => !row.pendingCapture).length;
  const metricDates = dates.length ? dates : [""];

  const loading = tmall.isLoading || kuaishou.isLoading || pdd.isLoading || douyin.isLoading;
  const failed = tmall.isError || kuaishou.isError || pdd.isError || douyin.isError;
  const demoSources = [tmall.data?.source, kuaishou.data?.source, pdd.data?.source, douyin.data?.source].filter((item) => item === "DEMO").length;

  const refresh = () => void Promise.all([tmall.refetch(), kuaishou.refetch(), pdd.refetch(), douyin.refetch()]);
  const selectedRecord = selected?.row.byDate.get(selected.date);
  const selectedIndex = selected ? dates.indexOf(selected.date) : -1;
  const previousRecord = selected && selectedIndex > 0 ? selected.row.byDate.get(dates[selectedIndex - 1]!) : undefined;

  return (
    <main className="main dailyAgentMain">
      <header className="topbar"><button className="menuButton" aria-label="打开导航" onClick={onMenuOpen}><Menu /></button><strong>客服每日对比</strong><span>所有平台客服日级经营指标</span></header>
      <div className="dailyAgentContent">
        <section className="dailyAgentHead">
          <div><h1>所有平台客服每日对比</h1><p>按平台、店铺和客服逐日查看询单量、响应与满意度。</p></div>
          <div className="dailyAgentActions"><button className="button" onClick={() => exportRows(rows.filter((row) => !row.pendingCapture), dates)} disabled={!visibleAgentCount || !dates.length}><Download />导出</button><button className="button primary" onClick={refresh} disabled={loading}><RefreshCw className={loading ? "spin" : ""} />刷新全部平台</button></div>
        </section>

        <nav className="dailyAgentPlatforms" aria-label="平台筛选">
          <button className={platform === "all" ? "active" : ""} onClick={() => { setPlatform("all"); setStore(""); }}><span>全部平台</span>{" "}<small>{storeDirectory.length}店</small></button>
          {platforms.map((item) => <button key={item.id} className={platform === item.id ? "active" : ""} onClick={() => { setPlatform(item.id); setStore(""); }}><span>{item.label}</span>{" "}<small>{platformStoreCounts[item.id]}店</small></button>)}
        </nav>

        {failed && <section className="dailyAgentNotice error"><strong>部分平台暂时无法连接</strong><span>其余平台数据仍正常展示，可点击“刷新全部平台”重试。</span></section>}

        <section className="dailyAgentPanel">
          <header>
            <div><h2>客服经营指标每日对比</h2><p>{visibleAgentCount} 位平台客服 · {activeStart && activeEnd ? `${shortDate(activeStart)} 至 ${shortDate(activeEnd)}` : "暂无日期"}</p></div>
            <div className="dailyAgentFilters">
              <label className="dailyAgentDate"><span>开始</span><input type="date" value={activeStart} min={availableDates[0]} max={activeEnd || availableDates.at(-1)} onChange={(event) => setStartDate(event.target.value)} /></label>
              <label className="dailyAgentDate"><span>结束</span><input type="date" value={activeEnd} min={activeStart || availableDates[0]} max={availableDates.at(-1)} onChange={(event) => setEndDate(event.target.value)} /></label>
              <select aria-label="店铺筛选" value={store} onChange={(event) => setStore(event.target.value)}><option value="">全部店铺</option>{stores.map((item) => <option key={item}>{item}</option>)}</select>
              <label className="dailyAgentSearch"><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索客服或店铺" /></label>
            </div>
          </header>
          <div className="dailyAgentTableScroll">
            <table
              className="dailyAgentTable equalColumns"
              style={{ minWidth: `${(2 + 3 * metricDates.length) * 140}px` }}
            >
              <colgroup>
                <col />
                <col />
                {(Object.keys(metricLabels) as MetricId[]).flatMap((item) => (
                  metricDates.map((date) => <col key={`${item}-${date || "empty"}`} />)
                ))}
              </colgroup>
              <thead>
                <tr><th className="dailyAgentStoreHead" rowSpan={2}>店铺</th><th className="dailyAgentAgentHead" rowSpan={2}>客服名</th>{(Object.keys(metricLabels) as MetricId[]).map((item) => <th key={item} className={`metricGroup ${item}`} colSpan={Math.max(dates.length, 1)}>{metricLabels[item]}</th>)}</tr>
                <tr>{(Object.keys(metricLabels) as MetricId[]).flatMap((item) => dates.length ? dates.map((date) => <th className="dailyAgentDateHead" key={`${item}-${date}`}>{shortDate(date)}</th>) : [<th className="dailyAgentDateHead" key={`${item}-empty`}>暂无日期</th>])}</tr>
              </thead>
              <tbody>{rows.map((row) => <tr key={row.key}>
                <td className="dailyAgentStoreCell" title={row.storeName}><div><span className={`platformBadge ${row.platform}`}>{platformLabels[row.platform]}</span><strong>{row.storeName}</strong></div></td>
                <td className="dailyAgentAgentCell"><strong>{row.agentName}</strong></td>
                {(Object.keys(metricLabels) as MetricId[]).flatMap((item) => metricDates.map((date) => {
                  if (!date) return <td className="dailyAgentMetricCell" key={`${item}-empty`}><span className="dailyAgentValue empty">--</span></td>;
                  const value = valueOf(row.byDate.get(date), item);
                  return <td className="dailyAgentMetricCell" key={`${item}-${date}`}><button className={`dailyAgentValue ${toneOf(value, item)}`} disabled={!row.byDate.has(date)} onClick={() => setSelected({ row, date, metric: item })}>{formatMetric(value, item)}</button></td>;
                }))}
              </tr>)}</tbody>
            </table>
            {!loading && !rows.length && <div className="dailyAgentEmpty">{platform !== "all" && !platforms.find((item) => item.id === platform)?.connected ? `${platformLabels[platform]}店铺目录和数据接口待接入，接入后会在这里展示。` : "当前筛选范围没有匹配的店铺。"}</div>}
            {loading && <div className="dailyAgentEmpty"><RefreshCw className="spin" />正在读取各平台每日数据...</div>}
          </div>
          <footer><span>“--”表示平台接口未返回该项或当天未采集，不会按 0 计算。{demoSources > 0 ? " 当前仍包含模拟来源，真实采集接入后自动替换。" : ""}</span><span>横向滚动查看全部指标和日期。</span></footer>
        </section>
      </div>

      {selected && selectedRecord && <div className="modalLayer" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelected(null); }}><section className="dailyAgentModal">
        <header><div><strong>{selected.row.agentName} · {shortDate(selected.date)}</strong><span>{platformLabels[selected.row.platform]} / {selected.row.storeName} / 当前查看：{metricLabels[selected.metric]}</span></div><button className="iconButton" aria-label="关闭" onClick={() => setSelected(null)}><X /></button></header>
        <div><MetricDetail label="询单量" metric="inquiry" current={selectedRecord} previous={previousRecord} /><MetricDetail label="平均响应" metric="response" current={selectedRecord} previous={previousRecord} /><MetricDetail label="满意度" metric="satisfaction" current={selectedRecord} previous={previousRecord} /></div>
        <footer><span>采集时间：{new Date(selectedRecord.collectedAt).toLocaleString("zh-CN")}</span><button className="button" onClick={() => setSelected(null)}>关闭</button></footer>
      </section></div>}
    </main>
  );
}

function MetricDetail({ label, metric, current, previous }: { label: string; metric: MetricId; current: DailyRecord; previous: DailyRecord | undefined }) {
  const currentValue = valueOf(current, metric);
  const previousValue = valueOf(previous, metric);
  const delta = currentValue !== null && previousValue !== null ? currentValue - previousValue : null;
  const positiveIsGood = metric !== "response";
  const deltaClass = delta === null || delta === 0 ? "flat" : (delta > 0) === positiveIsGood ? "up" : "down";
  return <article><span>{label}</span><strong>{formatMetric(currentValue, metric)}</strong><small className={deltaClass}>{delta === null ? "无前一日可比数据" : delta === 0 ? "较前一日持平" : `较前一日${delta > 0 ? "上升" : "下降"} ${formatMetric(Math.abs(delta), metric)}`}</small></article>;
}
